// „Termine" — Outlook-Termine (Graph) aus dem EIGENEN Account-Kalender des
// angemeldeten Users + TWV (Vorgänge/Todos), umschaltbar Heute-Liste /
// Wochenboard. Das Board zeigt ein 3-Tage-Fenster (Desktop) bzw. einen
// horizontalen 7-Tage-Streifen (Phone) — beides über die bereits geladene
// Woche Mo–So, per ◀ ▶-Pfeilen bzw. Touch-Scroll erreichbar. Die
// Termine-Quelle ist bewusst der persönliche Kalender (über user.email),
// NICHT die geteilte Overlay-Auswahl des Kalender-Moduls.
//
// T-394 (26.10.2026, PO: „als Popup"): Kopf und Freifläche einer Board-Spalte
// öffnen den Tag als Popup (`DashTagPopup`); die Termin-Chips öffnen weiter nur
// ihre Vorschau. Ganztägige Doppel (Feiertage aus mehreren Kalendern) stehen
// je Tag nur einmal da.
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
    CalendarLtr20Regular,
    ChevronLeft20Regular,
    ChevronRight20Regular,
} from "@fluentui/react-icons";
import { calendarService, type SourcedCalendarEvent } from "../../services/calendarService";
import { vorgangService, type TwvItemDto } from "../../services/vorgangService";
import { istGeburtstagEvent, istAbwesenheitsEvent } from "../calendar/eventHeuristics";
import type { PeekAnchor } from "../calendar/peek";
import { useAuth } from "../../context/AuthContext";
import { useResponsive } from "../../hooks/useResponsive";
import { REFINED } from "../risikoanalyseStep/tokens";
import { DashCard, Segmented, EmptyHint, deTime, toGraphDate, startOfDay, isToday } from "./DashboardShared";
import { DashTerminPopover, type DashTerminPeek } from "./DashTerminPopover";
import { DashTagPopup } from "./DashTagPopup";
import { GanztaegigPill, Row } from "./TerminRow";
import { fasseGanztaegigeZusammen } from "./termineTag";

type View = "heute" | "woche";

type WeekCol = {
    wd: string;
    date: Date;
    key: string;
    dayEvents: SourcedCalendarEvent[];
    dayTwv: TwvItemDto[];
    isToday: boolean;
};

const GOLD = "#e6a417";

const mondayOf = (ref: Date): Date => {
    const d = startOfDay(ref);
    const wd = (d.getDay() + 6) % 7; // Mo=0 … So=6
    d.setDate(d.getDate() - wd);
    return d;
};

const dayKey = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const WEEKDAYS_FULL = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

// Kurzform für die Fenster-Beschriftung des Boards, z. B. „Mi 8.7.".
const fmtDay = (c: WeekCol): string => `${c.wd} ${c.date.getDate()}.${c.date.getMonth() + 1}.`;

// Default-Fensterstart des 3-Tage-Boards über der Woche Mo–So: beginnt „heute",
// geklemmt auf [0..4], damit stets 3 Tage passen (Fr/Sa/So → Fr–Sa–So). Das
// heutige Fenster enthält damit immer den heutigen Tag. Export nur zum Testen.
export const defaultBoardOffset = (monday: Date, now: Date): number => {
    const diff = Math.round((startOfDay(now).getTime() - startOfDay(monday).getTime()) / 86_400_000);
    return Math.min(4, Math.max(0, diff));
};

export const TermineWiedervorlagenCard: React.FC = () => {
    const navigate = useNavigate();
    const { isPhone } = useResponsive();
    const { user } = useAuth();
    const [view, setView] = useState<View>("heute");
    // P3: im Woche-Modus zwischen kompaktem 3-Tage-Board und chronologischer
    // Wochen-Agenda (Mo–So, leere Tage ausgeblendet) umschalten. Die Agenda
    // bleibt als Wochenüberblick erhalten (Entscheidung 7).
    const [weekMode, setWeekMode] = useState<"board" | "agenda">("board");
    const [events, setEvents] = useState<SourcedCalendarEvent[]>([]);
    const [twv, setTwv] = useState<TwvItemDto[]>([]);
    const [loading, setLoading] = useState(false);

    // Eigene Postfach-Adresse des angemeldeten Users — die Termine-Karte zeigt
    // ausschließlich den persönlichen Graph-Kalender, unabhängig von der
    // geteilten Overlay-Auswahl im Kalender-Modul.
    const ownAddress = useMemo(() => (user?.email ?? "").trim().toLowerCase(), [user?.email]);

    const monday = useMemo(() => mondayOf(new Date()), []);

    // Fensterstart des 3-Tage-Boards (Desktop): Index 0..4 über der Woche Mo–So.
    // Default: das Fenster beginnt heute, geklemmt auf max. Fr, damit stets 3
    // Tage passen (z. B. Fr → Fr–Sa–So). Die Pfeile blättern ±1 Tag
    // (Entscheidung 8); Grenzen Mo/So deaktivieren den jeweiligen Pfeil.
    const [offset, setOffset] = useState<number>(() => defaultBoardOffset(monday, new Date()));

    // Phone: der 7-Tage-Streifen wird per Touch gescrollt; die Pfeile scrollen
    // ihn programmatisch (kein Fenster-Offset).
    const stripRef = useRef<HTMLDivElement | null>(null);
    const scrollStrip = (dir: number) => {
        const el = stripRef.current;
        if (!el) return;
        const first = el.firstElementChild as HTMLElement | null;
        const step = first ? first.offsetWidth + 12 : el.clientWidth * 0.8;
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    // Interaktion Graph-Termine (SP5, A10): 1 Klick → Übersichts-Popover (nach
    // ~250 ms), Doppelklick → Navigation zum Termin im KMVS-Kalender. Die
    // Entprellung sorgt dafür, dass ein Doppelklick nicht zusätzlich das Popover
    // auslöst; TWV-Zeilen behalten ihre sofortige Direkt-Navigation (kein Timer).
    const [peek, setPeek] = useState<DashTerminPeek | null>(null);
    const clickTimerRef = useRef<number | null>(null);
    const clearClickTimer = () => {
        if (clickTimerRef.current !== null) {
            window.clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
        }
    };
    useEffect(() => () => clearClickTimer(), []);

    const onEventClick = (ev: SourcedCalendarEvent, e: React.MouseEvent) => {
        // Anker = rechte Oberkante der angeklickten Zeile/Kachel (Outlook-Peek-
        // Position); das Popover wird per clampPopoverPosition in den Viewport
        // geklemmt.
        const r = e.currentTarget.getBoundingClientRect();
        const anchor: PeekAnchor = { x: r.right, y: r.top };
        clearClickTimer();
        clickTimerRef.current = window.setTimeout(() => {
            clickTimerRef.current = null;
            setPeek({ event: ev, anchor });
        }, 250);
    };

    // Doppelklick / „Im Kalender öffnen" → Tages-Deep-Link mit event/mailbox;
    // CalendarDeepLink öffnet nach dem Load das Termin-Fenster (SP5). Das Datum
    // ist der lokale Tag des Termins (dayKey), damit die Tagesansicht passt.
    const navToTermin = (ev: SourcedCalendarEvent) => {
        const date = dayKey(toGraphDate(ev.start.dateTime));
        navigate(
            `/termine?date=${date}&event=${encodeURIComponent(ev.id)}&mailbox=${encodeURIComponent(
                ev.source.address,
            )}`,
        );
    };

    const onEventDoubleClick = (ev: SourcedCalendarEvent) => {
        clearClickTimer();
        setPeek(null);
        navToTermin(ev);
    };

    const onOpenInCalendar = (ev: SourcedCalendarEvent) => {
        setPeek(null);
        navToTermin(ev);
    };

    // T-394: der geöffnete Tag des Boards (Schlüssel „YYYY-MM-DD"). Die Daten
    // kommen bei jedem Rendern aus `week7` — so bleibt das Popup aktuell.
    const [offenerTag, setOffenerTag] = useState<string | null>(null);
    const schliesseTag = React.useCallback(() => setOffenerTag(null), []);

    useEffect(() => {
        let cancelled = false;
        vorgangService.getMyTwv().then((t) => { if (!cancelled) setTwv(t); }).catch(() => { if (!cancelled) setTwv([]); });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!ownAddress) { setEvents([]); return; }
        let cancelled = false;
        setLoading(true);
        const from = monday;
        const to = new Date(monday);
        to.setDate(to.getDate() + 7);
        calendarService
            .listEventsForMailboxes(
                [{ address: ownAddress, displayName: ownAddress, color: REFINED.aub }],
                from,
                to,
                // top=500 statt 200: stark „gefoldert" bestückte Kalender (z. B.
                // dc@courte.com mit vielen ganztägigen Geburtstags-Events) sonst
                // die Woche vollmachen und echte Termine über die 200er-Kappung
                // schieben. Gefiltert wird erst im FE (istGeburtstagEvent /
                // istAbwesenheitsEvent) — der höhere top hält echte Termine drin.
                500,
            )
            .then((list) => { if (!cancelled) setEvents(list); })
            .catch(() => { if (!cancelled) setEvents([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [ownAddress, monday]);

    // Geburtstags- und Abwesenheits-Events (ganztägige Outlook-Einträge, v. a. bei
    // dc@courte.com) NUR aus dieser Dashboard-Karte herausfiltern (A4) — wirkt auf
    // Heute UND Woche. Geburtstage tauchen stattdessen in der Geburtstage-Karte auf
    // (SP2); der Kalender (/termine) bleibt unberührt. Keyword-Set = Backend
    // (inkl. „frei", Entscheidung 4) via geteilter Heuristik.
    const visibleEvents = useMemo(
        () =>
            events.filter(
                (e) =>
                    !istGeburtstagEvent(e.subject, e.isAllDay || false) &&
                    !istAbwesenheitsEvent(e.subject, e.isAllDay || false),
            ),
        [events],
    );

    /* ── Heute ── */
    const heuteEvents = useMemo(
        () => fasseGanztaegigeZusammen(
            visibleEvents
                .filter((e) => isToday(e.start.dateTime))
                .sort((a, b) => toGraphDate(a.start.dateTime).getTime() - toGraphDate(b.start.dateTime).getTime()),
        ),
        [visibleEvents],
    );
    const heuteTwv = useMemo(() => twv.filter((t) => isToday(t.twv)), [twv]);

    // To-dos haben keine eigene Detailseite mehr — beide Zeilen-Arten führen auf
    // den Vorgang. `TwvItemDto.vorgangId` liefert das Backend für To-dos aus der
    // geladenen Relation mit (bei Vorgängen ist es die eigene Id). Fehlt sie
    // wider Erwarten, passiert nichts — statt auf `/vorgaenge/undefined` zu gehen.
    const goTwv = (t: TwvItemDto) => {
        const ziel = t.kind === "vorgang" ? t.id : t.vorgangId;
        if (ziel != null) navigate(`/vorgaenge/${ziel}`);
    };

    /* ── Woche ── */
    // Volle Woche Mo–So als Board-Datengrundlage (Desktop-Fenster + Phone-Streifen).
    const week7 = useMemo<WeekCol[]>(() => {
        const todayKey = dayKey(startOfDay(new Date()));
        return WEEKDAYS_FULL.map((wd, i) => {
            const date = new Date(monday);
            date.setDate(date.getDate() + i);
            const key = dayKey(date);
            const dayEvents = fasseGanztaegigeZusammen(
                visibleEvents
                    .filter((e) => dayKey(toGraphDate(e.start.dateTime)) === key)
                    .sort((a, b) => toGraphDate(a.start.dateTime).getTime() - toGraphDate(b.start.dateTime).getTime()),
            );
            const dayTwv = twv.filter((t) => dayKey(new Date(/^\d{4}-\d{2}-\d{2}/.test(t.twv) ? t.twv + "T00:00:00" : t.twv)) === key);
            return { wd, date, key, dayEvents, dayTwv, isToday: key === todayKey };
        });
    }, [monday, visibleEvents, twv]);

    // Beschriftung des aktuellen Desktop-Fensters, z. B. „Mi 8.7. – Fr 10.7.".
    const windowLabel = useMemo(() => {
        const a = week7[offset];
        const b = week7[Math.min(6, offset + 2)];
        return a && b ? `${fmtDay(a)} – ${fmtDay(b)}` : "";
    }, [week7, offset]);

    // P3: chronologische Wochen-Agenda Mo–So; Tage ohne Einträge ausgeblendet.
    const weekAgenda = useMemo<WeekCol[]>(
        () => week7.filter((col) => col.dayEvents.length > 0 || col.dayTwv.length > 0),
        [week7],
    );

    const noOwnCalendar = !ownAddress;

    const tagSpalte = offenerTag ? week7.find((c) => c.key === offenerTag) ?? null : null;

    return (
        <DashCard
            title="Termine"
            icon={<CalendarLtr20Regular />}
            right={<Segmented options={[{ key: "heute", label: "Heute" }, { key: "woche", label: "Woche" }]} value={view} onChange={setView} />}
        >
            {view === "heute" ? (
                <div style={{ paddingTop: 8 }}>
                    {loading && heuteEvents.length === 0 && heuteTwv.length === 0 ? (
                        <EmptyHint>Lade Termine…</EmptyHint>
                    ) : heuteEvents.length === 0 && heuteTwv.length === 0 ? (
                        <EmptyHint>{noOwnCalendar ? "Eigener Kalender nicht verfügbar." : "Heute keine Termine oder Wiedervorlagen."}</EmptyHint>
                    ) : (
                        <div>
                            {heuteEvents.map((e, idx) => (
                                <Row
                                    key={`ev-${e.source.address}-${e.id}`}
                                    border={idx > 0}
                                    time={e.isAllDay ? "" : deTime(e.start.dateTime)}
                                    allDay={e.isAllDay || false}
                                    dot={e.source.color || REFINED.aub}
                                    title={e.subject || "(ohne Titel)"}
                                    sub={e.location || ""}
                                    chip={{ kind: "termin", online: e.isOnlineMeeting }}
                                    onClick={(me) => onEventClick(e, me)}
                                    onDoubleClick={() => onEventDoubleClick(e)}
                                />
                            ))}
                            {heuteTwv.map((t, idx) => (
                                <Row
                                    key={`twv-${t.kind}-${t.id}`}
                                    border={idx > 0 || heuteEvents.length > 0}
                                    time="—"
                                    dot={GOLD}
                                    title={t.titel}
                                    sub=""
                                    chip={{ kind: "twv" }}
                                    onClick={() => goTwv(t)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ paddingTop: 12 }}>
                    {/* P3: Umschalter 3-Tage-Board ↔ Wochen-Agenda (Mo–So). */}
                    <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 12 }}>
                        <Segmented
                            options={[{ key: "board", label: "Board" }, { key: "agenda", label: "Agenda" }]}
                            value={weekMode}
                            onChange={setWeekMode}
                        />
                    </div>
                    {weekMode === "board" ? (
                        isPhone ? (
                            // Phone (≤768px): horizontaler 7-Tage-Streifen mit Scroll-Snap;
                            // Daumen-Scroll, die Pfeile scrollen den Streifen programmatisch.
                            <div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, paddingBottom: 10 }}>
                                    <ArrowBtn dir="left" onClick={() => scrollStrip(-1)} />
                                    <div style={{ fontFamily: REFINED.fontUI, fontWeight: 800, fontSize: "0.8rem", color: REFINED.ink2 }}>Mo – So</div>
                                    <ArrowBtn dir="right" onClick={() => scrollStrip(1)} />
                                </div>
                                <div
                                    ref={stripRef}
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(7, minmax(132px, 1fr))",
                                        gap: 12,
                                        overflowX: "auto",
                                        WebkitOverflowScrolling: "touch",
                                        scrollSnapType: "x mandatory",
                                        marginLeft: -2,
                                        paddingLeft: 2,
                                        paddingRight: 2,
                                        paddingBottom: 2,
                                    }}
                                >
                                    {week7.map((col) => (
                                        <BoardColumn
                                            key={col.key}
                                            col={col}
                                            onOpen={() => setOffenerTag(col.key)}
                                            onTwv={goTwv}
                                            onEventClick={onEventClick}
                                            onEventDoubleClick={onEventDoubleClick}
                                            snap
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            // Desktop/Tablet: 3-Tage-Fenster über der Woche Mo–So, per ◀ ▶
                            // tageweise blätterbar. Bei voller Kartenbreite deutlich breitere
                            // Spalten als das frühere Mo–Fr-Board.
                            <div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, paddingBottom: 10 }}>
                                    <ArrowBtn dir="left" disabled={offset <= 0} onClick={() => setOffset((o) => Math.max(0, o - 1))} />
                                    <div style={{ fontFamily: REFINED.fontUI, fontWeight: 800, fontSize: "0.8rem", color: REFINED.ink2, textAlign: "center" }}>
                                        {windowLabel}
                                    </div>
                                    <ArrowBtn dir="right" disabled={offset >= 4} onClick={() => setOffset((o) => Math.min(4, o + 1))} />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                                    {week7.slice(offset, offset + 3).map((col) => (
                                        <BoardColumn
                                            key={col.key}
                                            col={col}
                                            onOpen={() => setOffenerTag(col.key)}
                                            onTwv={goTwv}
                                            onEventClick={onEventClick}
                                            onEventDoubleClick={onEventDoubleClick}
                                        />
                                    ))}
                                </div>
                            </div>
                        )
                    ) : weekAgenda.length === 0 ? (
                        <EmptyHint>{noOwnCalendar ? "Eigener Kalender nicht verfügbar." : "Diese Woche keine Termine oder Wiedervorlagen."}</EmptyHint>
                    ) : (
                        <div>
                            {weekAgenda.map((col) => (
                                <div key={col.key} style={{ paddingTop: 6 }}>
                                    <div
                                        style={{
                                            fontFamily: REFINED.fontUI,
                                            fontWeight: 800,
                                            fontSize: "0.78rem",
                                            letterSpacing: "0.04em",
                                            textTransform: "uppercase",
                                            color: col.isToday ? GOLD : REFINED.ink3,
                                            padding: "12px 6px 2px",
                                        }}
                                    >
                                        {col.wd}, {col.date.getDate()}.{col.date.getMonth() + 1}.
                                    </div>
                                    {col.dayEvents.map((e, idx) => (
                                        <Row
                                            key={`ae-${e.source.address}-${e.id}`}
                                            border={idx > 0}
                                            time={e.isAllDay ? "" : deTime(e.start.dateTime)}
                                            allDay={e.isAllDay || false}
                                            dot={e.source.color || REFINED.aub}
                                            title={e.subject || "(ohne Titel)"}
                                            sub={e.location || ""}
                                            chip={{ kind: "termin", online: e.isOnlineMeeting }}
                                            onClick={(me) => onEventClick(e, me)}
                                            onDoubleClick={() => onEventDoubleClick(e)}
                                        />
                                    ))}
                                    {col.dayTwv.map((t, idx) => (
                                        <Row
                                            key={`at-${t.kind}-${t.id}`}
                                            border={idx > 0 || col.dayEvents.length > 0}
                                            time="—"
                                            dot={GOLD}
                                            title={t.titel}
                                            sub=""
                                            chip={{ kind: "twv" }}
                                            onClick={() => goTwv(t)}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {/* SP5 (A10): Klick-Popover eines Graph-Termins — Organisator/
                Teilnehmer/Uhrzeit + „Im Kalender öffnen". Portal an document.body,
                daher nicht vom overflow:hidden der Karte beschnitten. */}
            {/* T-394: ein Tag des Boards als Popup (Handy: Bottom-Sheet). Die
                Vorschau eines Termins daraus liegt darüber. */}
            <DashTagPopup
                tag={
                    tagSpalte
                        ? {
                              date: tagSpalte.date,
                              key: tagSpalte.key,
                              isToday: tagSpalte.isToday,
                              events: tagSpalte.dayEvents,
                              twv: tagSpalte.dayTwv,
                          }
                        : null
                }
                isPhone={isPhone}
                vorschauOffen={peek !== null}
                onClose={schliesseTag}
                onEventClick={onEventClick}
                onEventDoubleClick={onEventDoubleClick}
                onTwv={goTwv}
                onOpenCalendar={(key) => {
                    setOffenerTag(null);
                    navigate(`/termine?date=${key}`);
                }}
            />
            <DashTerminPopover
                peek={peek}
                ownAddress={ownAddress}
                onClose={() => setPeek(null)}
                onOpenInCalendar={onOpenInCalendar}
            />
        </DashCard>
    );
};

// Schmaler 32px-Blätter-Button im Karten-Stil (◀ ▶).
const ArrowBtn: React.FC<{ dir: "left" | "right"; disabled?: boolean; onClick: () => void }> = ({ dir, disabled, onClick }) => (
    <button
        type="button"
        aria-label={dir === "left" ? "Zurück" : "Weiter"}
        onClick={onClick}
        disabled={disabled}
        style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            border: `1px solid ${REFINED.line}`,
            backgroundColor: REFINED.white,
            color: disabled ? REFINED.ink3 : REFINED.ink,
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.45 : 1,
            padding: 0,
        }}
    >
        {dir === "left" ? <ChevronLeft20Regular /> : <ChevronRight20Regular />}
    </button>
);

// Eine Tagesspalte des Boards (Desktop-Fenster wie Phone-Streifen). `snap`
// setzt scroll-snap-align für den Phone-Streifen. Kopf und Freifläche öffnen
// den Tag (`onOpen`, T-394); die Chips halten ihren Klick bei sich. Der Kopf
// ist ein echter Knopf — so geht es auch per Tastatur; sein Klick läuft wie
// jeder andere in der Spalte zum `onClick` der Spalte hoch.
const BoardColumn: React.FC<{
    col: WeekCol;
    onOpen: () => void;
    onTwv: (t: TwvItemDto) => void;
    onEventClick: (ev: SourcedCalendarEvent, e: React.MouseEvent) => void;
    onEventDoubleClick: (ev: SourcedCalendarEvent) => void;
    snap?: boolean;
}> = ({ col, onOpen, onTwv, onEventClick, onEventDoubleClick, snap }) => (
    <div
        onClick={onOpen}
        title="Tag öffnen"
        style={{
            borderRadius: 14,
            border: `1px solid ${col.isToday ? GOLD : REFINED.line}`,
            backgroundColor: col.isToday ? "#fdf4e0" : "#faf7fb",
            padding: 10,
            minHeight: 120,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            cursor: "pointer",
            ...(snap ? { scrollSnapAlign: "start" } : {}),
        }}
    >
        <button
            type="button"
            aria-label={`Tag öffnen: ${col.wd}, ${col.date.getDate()}.${col.date.getMonth() + 1}.`}
            style={{
                display: "block",
                width: "100%",
                textAlign: "center",
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
                font: "inherit",
            }}
        >
            <div style={{ fontWeight: 800, color: REFINED.ink, fontSize: "0.9rem" }}>{col.wd}</div>
            <div style={{ color: REFINED.ink3, fontSize: "0.78rem" }}>
                {col.date.getDate()}.{col.date.getMonth() + 1}
            </div>
        </button>
        {col.dayEvents.length === 0 && col.dayTwv.length === 0 ? (
            <div style={{ flex: 1 }} />
        ) : (
            <>
                {col.dayEvents.map((e) => (
                    <Chip
                        key={`we-${e.source.address}-${e.id}`}
                        color={REFINED.aub}
                        allDay={e.isAllDay || false}
                        top={e.isAllDay ? "" : deTime(e.start.dateTime)}
                        title={e.subject || "(ohne Titel)"}
                        onClick={(me) => onEventClick(e, me)}
                        onDoubleClick={() => onEventDoubleClick(e)}
                    />
                ))}
                {col.dayTwv.map((t) => (
                    <Chip key={`wt-${t.kind}-${t.id}`} color={GOLD} top="TWV" title={t.titel} onClick={() => onTwv(t)} />
                ))}
            </>
        )}
    </div>
);

const Chip: React.FC<{
    color: string;
    top: string;
    allDay?: boolean;
    title: string;
    onClick?: (e: React.MouseEvent) => void;
    onDoubleClick?: () => void;
}> = ({ color, top, allDay, title, onClick, onDoubleClick }) => (
    <div
        // Der Klick gehört dem Chip — nicht der Spalte, die sonst den Tag öffnete (T-394).
        onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
        }}
        onDoubleClick={(e) => {
            e.stopPropagation();
            onDoubleClick?.();
        }}
        style={{
            backgroundColor: allDay ? REFINED.aub50 : REFINED.white,
            borderRadius: 8,
            borderLeft: `3px solid ${color}`,
            boxShadow: "0 1px 3px rgba(58,12,49,0.08)",
            padding: "6px 8px",
            cursor: onClick || onDoubleClick ? "pointer" : "default",
        }}
    >
        {allDay ? (
            <div style={{ marginBottom: 2 }}><GanztaegigPill /></div>
        ) : (
            <div style={{ fontSize: "0.78rem", color: REFINED.ink2, fontWeight: 800 }}>{top}</div>
        )}
        <div style={{ fontSize: "0.82rem", color: REFINED.ink, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</div>
    </div>
);

export default TermineWiedervorlagenCard;
